package org.teddapp.project.db

import app.cash.sqldelight.db.SqlDriver
import com.tedd.db.TeddDatabase

expect class DatabaseDriverFactory {
    fun createDriver(): SqlDriver
}

fun createTeddDatabase(factory: DatabaseDriverFactory): TeddDatabase {
    val driver = factory.createDriver()
    applyTeddMigrations(driver)
    return TeddDatabase(driver)
}

/**
 * Rattrapages à faire une fois la base ouverte : les micro-tâches récurrentes oubliées
 * reviennent aujourd'hui plutôt que de s'accumuler en retard.
 */
suspend fun runTeddCatchUp(db: TeddDatabase) {
    RecurrenceScheduler.catchUp(db)
}

/**
 * Migrations idempotentes appliquées à chaque ouverture, en l'absence de système
 * de versions SQLDelight (la création initiale passe par Schema.create dans chaque
 * factory). Chaque ALTER est protégé par try/catch : sur une base neuve la colonne
 * existe déjà (créée par le schéma), l'erreur "duplicate column" est alors ignorée.
 */
private fun applyTeddMigrations(driver: SqlDriver) {
    // Dossiers/sous-dossiers : notebook.parent_id (auto-référence).
    tryExec(driver, "ALTER TABLE notebook ADD COLUMN parent_id TEXT")
    // Objectifs (texte) du projet.
    tryExec(driver, "ALTER TABLE project ADD COLUMN objectives TEXT")
    // Révisions (répétition espacée). Nouvelles tables pour les bases existantes.
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS review_card (" +
            "id TEXT NOT NULL PRIMARY KEY, title TEXT NOT NULL, content TEXT NOT NULL DEFAULT '', " +
            "topic TEXT, box INTEGER NOT NULL DEFAULT 0, next_date TEXT NOT NULL, last_reviewed_at INTEGER, " +
            "order_index INTEGER NOT NULL DEFAULT 0, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS review_log (" +
            "id TEXT NOT NULL PRIMARY KEY, card_id TEXT NOT NULL, date TEXT NOT NULL, " +
            "grade TEXT NOT NULL DEFAULT 'good', from_box INTEGER NOT NULL DEFAULT 0, to_box INTEGER NOT NULL DEFAULT 0, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    // review_log : colonnes grade/from_box/to_box pour les bases déjà créées (ancienne forme `rating`).
    tryExec(driver, "ALTER TABLE review_log ADD COLUMN grade TEXT NOT NULL DEFAULT 'good'")
    tryExec(driver, "ALTER TABLE review_log ADD COLUMN from_box INTEGER NOT NULL DEFAULT 0")
    tryExec(driver, "ALTER TABLE review_log ADD COLUMN to_box INTEGER NOT NULL DEFAULT 0")
    // Journal de séances (discipline).
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS activity_session (" +
            "id TEXT NOT NULL PRIMARY KEY, date TEXT NOT NULL, time TEXT NOT NULL DEFAULT '', " +
            "activity_type TEXT NOT NULL, title TEXT, duration_min INTEGER NOT NULL DEFAULT 0, " +
            "intensity INTEGER NOT NULL DEFAULT 3, mood INTEGER NOT NULL DEFAULT 4, distance REAL, pages INTEGER, note TEXT, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    // Déclencheurs périodiques de transfert (Finance).
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS finance_trigger (" +
            "id TEXT NOT NULL PRIMARY KEY, name TEXT NOT NULL, icon TEXT NOT NULL, color TEXT NOT NULL, " +
            "frequency TEXT NOT NULL, anchor_day INTEGER, next_run_date TEXT NOT NULL, last_run_date TEXT, " +
            "is_active INTEGER NOT NULL DEFAULT 1, order_index INTEGER NOT NULL DEFAULT 0, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS finance_trigger_leg (" +
            "id TEXT NOT NULL PRIMARY KEY, trigger_id TEXT NOT NULL, source_type TEXT NOT NULL, source_id TEXT NOT NULL, " +
            "dest_account_id TEXT NOT NULL, dest_fund_id TEXT, amount INTEGER NOT NULL DEFAULT 0, order_index INTEGER NOT NULL DEFAULT 0, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    // Listes de dépenses périodiques : colonnes de récurrence.
    tryExec(driver, "ALTER TABLE expense_list ADD COLUMN recurrence TEXT NOT NULL DEFAULT 'once'")
    tryExec(driver, "ALTER TABLE expense_list ADD COLUMN next_reset_date TEXT")
    tryExec(driver, "ALTER TABLE expense_list ADD COLUMN last_reset_date TEXT")
    tryExec(driver, "ALTER TABLE expense_list ADD COLUMN reset_mode TEXT NOT NULL DEFAULT 'reset'")
    // Compte : seuils (limite basse + seuil positif), informatifs.
    tryExec(driver, "ALTER TABLE finance_account ADD COLUMN low_threshold INTEGER")
    tryExec(driver, "ALTER TABLE finance_account ADD COLUMN high_threshold INTEGER")
    // Chronomètre partagé entre appareils (refonte v2).
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS timer_state (" +
            "id TEXT NOT NULL PRIMARY KEY, running INTEGER NOT NULL DEFAULT 0, started_at INTEGER, " +
            "planned_minutes INTEGER NOT NULL DEFAULT 25, item_id TEXT, skill_id TEXT, paused_at INTEGER, " +
            "paused_accum_ms INTEGER NOT NULL DEFAULT 0, device TEXT, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    // Révision espacée : boîte de Leitner à six cases (refonte v2).
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS flashcard (" +
            "id TEXT NOT NULL PRIMARY KEY, skill_id TEXT, front TEXT NOT NULL, back TEXT NOT NULL, note TEXT, " +
            "box INTEGER NOT NULL DEFAULT 1, due_date TEXT NOT NULL, last_review TEXT, " +
            "reviews INTEGER NOT NULL DEFAULT 0, lapses INTEGER NOT NULL DEFAULT 0, order_index INTEGER NOT NULL DEFAULT 0, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    // Patrimoine : biens et constats de valeur (refonte v2).
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS asset (" +
            "id TEXT NOT NULL PRIMARY KEY, name TEXT NOT NULL, kind TEXT NOT NULL, " +
            "currency TEXT NOT NULL DEFAULT 'XOF', value INTEGER, purchase_price INTEGER, " +
            "acquired_date TEXT, valued_at TEXT, note TEXT, archived INTEGER NOT NULL DEFAULT 0, " +
            "order_index INTEGER NOT NULL DEFAULT 0, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS asset_valuation (" +
            "id TEXT NOT NULL PRIMARY KEY, asset_id TEXT NOT NULL, date TEXT NOT NULL, " +
            "value INTEGER NOT NULL, note TEXT, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    // Compétences (Kaufman) et leurs rattachements (refonte v2).
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS skill (" +
            "id TEXT NOT NULL PRIMARY KEY, name TEXT NOT NULL, color TEXT NOT NULL DEFAULT '#22508A', " +
            "icon TEXT NOT NULL DEFAULT 'book', intention TEXT, target_hours INTEGER NOT NULL DEFAULT 1000, " +
            "archived INTEGER NOT NULL DEFAULT 0, order_index INTEGER NOT NULL DEFAULT 0, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "ALTER TABLE focus_session ADD COLUMN skill_id TEXT")
    tryExec(driver, "ALTER TABLE objective ADD COLUMN skill_id TEXT")
    tryExec(driver, "ALTER TABLE project ADD COLUMN skill_id TEXT")
    // Correspondance entre identifiants locaux et UUID du serveur.
    tryExec(driver, "CREATE TABLE IF NOT EXISTS server_id_map (server_id TEXT NOT NULL PRIMARY KEY, local_id TEXT NOT NULL)")
    // Trousseau de secrets : tout y est chiffré sur l'appareil.
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS keychain (" +
            "id TEXT NOT NULL PRIMARY KEY, name TEXT NOT NULL, icon TEXT NOT NULL, color TEXT NOT NULL, " +
            "wrapped_key TEXT NOT NULL, kdf TEXT NOT NULL, order_index INTEGER NOT NULL DEFAULT 0, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS keychain_item (" +
            "id TEXT NOT NULL PRIMARY KEY, keychain_id TEXT NOT NULL, payload TEXT NOT NULL, " +
            "order_index INTEGER NOT NULL DEFAULT 0, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS keychain_item_keychain_idx ON keychain_item(keychain_id) WHERE is_deleted = 0")
    // Habitudes : observations d'envies (refonte v2).
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS craving_observation (" +
            "id TEXT NOT NULL PRIMARY KEY, habit TEXT NOT NULL, date TEXT NOT NULL, " +
            "sensations TEXT NOT NULL DEFAULT '', zones TEXT NOT NULL DEFAULT '', " +
            "intensity INTEGER NOT NULL DEFAULT 5, outcome TEXT NOT NULL, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    // Finance : verdict du filtre cartésien (refonte v2).
    tryExec(driver, "ALTER TABLE finance_transaction ADD COLUMN verdict TEXT")
    // Étiquettes de compétence : « données, ml, langage ».
    tryExec(driver, "ALTER TABLE skill ADD COLUMN tags TEXT")
    tryExec(driver, "ALTER TABLE skill ADD COLUMN pinned INTEGER NOT NULL DEFAULT 0")
    // Date butoir pour atteindre la cible d'heures.
    tryExec(driver, "ALTER TABLE skill ADD COLUMN deadline TEXT")
    // Focus : phrase de fin de course (refonte v2).
    tryExec(driver, "ALTER TABLE focus_session ADD COLUMN note TEXT")
    // Réflexions d'objectif (échéance passée, tentative numérotée). Refonte v2.
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS objective_reflection (" +
            "id TEXT NOT NULL PRIMARY KEY, objective_id TEXT NOT NULL, attempt INTEGER NOT NULL DEFAULT 1, " +
            "text TEXT NOT NULL, missed_deadline TEXT, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    // Parcours de compétence importés depuis un fichier : la suite ordonnée des étapes.
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS parcours_step (" +
            "id TEXT NOT NULL PRIMARY KEY, skill_id TEXT NOT NULL, code TEXT NOT NULL, kind TEXT NOT NULL, " +
            "position INTEGER NOT NULL DEFAULT 0, title TEXT NOT NULL, summary TEXT, " +
            "planned_min INTEGER NOT NULL DEFAULT 0, level_name TEXT, level_desc TEXT, project_id TEXT, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "ALTER TABLE objective ADD COLUMN step_id TEXT")
    tryExec(driver, "ALTER TABLE task ADD COLUMN kind TEXT")
    tryExec(driver, "ALTER TABLE task ADD COLUMN deliverable TEXT")
    tryExec(driver, "ALTER TABLE task ADD COLUMN acceptance TEXT")
    // Temps prévu d'une tâche rapide du Pomodoro.
    tryExec(driver, "ALTER TABLE pomodoro_item ADD COLUMN estimated_minutes INTEGER")
    // Portfolio & recueil de compétences : référentiel Famille → Compétence → Critères,
    // pièces de portfolio et liaisons pièce ↔ compétence démontrée.
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS competence_family (" +
            "id TEXT NOT NULL PRIMARY KEY, name TEXT NOT NULL, description TEXT, " +
            "color TEXT NOT NULL DEFAULT '#22508A', icon TEXT NOT NULL DEFAULT 'book', " +
            "order_index INTEGER NOT NULL DEFAULT 0, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS competence_family_order_idx ON competence_family(order_index) WHERE is_deleted = 0")
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS competence (" +
            "id TEXT NOT NULL PRIMARY KEY, family_id TEXT NOT NULL, skill_id TEXT, name TEXT NOT NULL, description TEXT, " +
            "order_index INTEGER NOT NULL DEFAULT 0, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS competence_family_idx ON competence(family_id, order_index) WHERE is_deleted = 0")
    tryExec(driver, "CREATE INDEX IF NOT EXISTS competence_skill_idx ON competence(skill_id) WHERE is_deleted = 0")
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS competence_criterion (" +
            "id TEXT NOT NULL PRIMARY KEY, competence_id TEXT NOT NULL, label TEXT NOT NULL, description TEXT, " +
            "level INTEGER NOT NULL DEFAULT 0, evaluated_at INTEGER, evaluation_note TEXT, " +
            "order_index INTEGER NOT NULL DEFAULT 0, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS competence_criterion_competence_idx ON competence_criterion(competence_id, order_index) WHERE is_deleted = 0")
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS portfolio_piece (" +
            "id TEXT NOT NULL PRIMARY KEY, title TEXT NOT NULL, subtitle TEXT, body TEXT NOT NULL DEFAULT '', " +
            "links TEXT, images TEXT, date_start TEXT, date_end TEXT, project_id TEXT, step_id TEXT, " +
            "status TEXT NOT NULL DEFAULT 'draft', pinned INTEGER NOT NULL DEFAULT 0, " +
            "order_index INTEGER NOT NULL DEFAULT 0, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS portfolio_piece_order_idx ON portfolio_piece(pinned, order_index) WHERE is_deleted = 0")
    tryExec(driver, "CREATE INDEX IF NOT EXISTS portfolio_piece_project_idx ON portfolio_piece(project_id) WHERE is_deleted = 0")
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS portfolio_piece_competence (" +
            "id TEXT NOT NULL PRIMARY KEY, piece_id TEXT NOT NULL, competence_id TEXT NOT NULL, criterion_id TEXT, note TEXT, " +
            "order_index INTEGER NOT NULL DEFAULT 0, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS portfolio_piece_competence_piece_idx ON portfolio_piece_competence(piece_id, order_index) WHERE is_deleted = 0")
    tryExec(driver, "CREATE INDEX IF NOT EXISTS portfolio_piece_competence_competence_idx ON portfolio_piece_competence(competence_id) WHERE is_deleted = 0")
    // Cartes heuristiques : la carte, ses nœuds en arbre, les notes de la bibliothèque rattachées.
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS mind_map (" +
            "id TEXT NOT NULL PRIMARY KEY, title TEXT NOT NULL, color TEXT, skill_id TEXT, objective_id TEXT, " +
            "order_index INTEGER NOT NULL DEFAULT 0, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS mind_map_order_idx ON mind_map(order_index, updated_at) WHERE is_deleted = 0")
    tryExec(driver, "CREATE INDEX IF NOT EXISTS mind_map_skill_idx ON mind_map(skill_id) WHERE is_deleted = 0 AND skill_id IS NOT NULL")
    tryExec(driver, "CREATE INDEX IF NOT EXISTS mind_map_objective_idx ON mind_map(objective_id) WHERE is_deleted = 0 AND objective_id IS NOT NULL")
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS mind_map_node (" +
            "id TEXT NOT NULL PRIMARY KEY, map_id TEXT NOT NULL, parent_id TEXT, text TEXT NOT NULL DEFAULT '', color TEXT, " +
            "source_kind TEXT, source_id TEXT, order_index INTEGER NOT NULL DEFAULT 0, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS mind_map_node_map_idx ON mind_map_node(map_id, order_index) WHERE is_deleted = 0")
    tryExec(driver, "CREATE INDEX IF NOT EXISTS mind_map_node_parent_idx ON mind_map_node(parent_id, order_index) WHERE is_deleted = 0")
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS mind_map_node_note (" +
            "id TEXT NOT NULL PRIMARY KEY, node_id TEXT NOT NULL, note_id TEXT NOT NULL, " +
            "order_index INTEGER NOT NULL DEFAULT 0, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS mind_map_node_note_node_idx ON mind_map_node_note(node_id, order_index) WHERE is_deleted = 0")
    tryExec(driver, "CREATE INDEX IF NOT EXISTS mind_map_node_note_note_idx ON mind_map_node_note(note_id) WHERE is_deleted = 0")
    // Fichiers joints (images des notes) et leurs octets sur l'appareil.
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS attachment (" +
            "id TEXT NOT NULL PRIMARY KEY, owner_kind TEXT NOT NULL, owner_id TEXT NOT NULL, " +
            "file_name TEXT NOT NULL, mime_type TEXT NOT NULL, size_bytes INTEGER NOT NULL, " +
            "storage_key TEXT, sha256 TEXT, width INTEGER, height INTEGER, " +
            "status TEXT NOT NULL DEFAULT 'pending', order_index INTEGER NOT NULL DEFAULT 0, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS attachment_owner_idx ON attachment(owner_kind, owner_id) WHERE is_deleted = 0")
    tryExec(driver, "CREATE TABLE IF NOT EXISTS file_blob (id TEXT NOT NULL PRIMARY KEY, bytes BLOB NOT NULL, kept_at INTEGER NOT NULL)")
    // Versions passées des notes, gardées par un déclencheur (voir NoteRevision.sq).
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS note_revision (" +
            "id TEXT NOT NULL PRIMARY KEY, note_id TEXT NOT NULL, title TEXT, body TEXT NOT NULL, " +
            "created_at INTEGER NOT NULL, kept_at INTEGER NOT NULL)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS note_revision_note_idx ON note_revision(note_id, kept_at)")
    tryExec(
        driver,
        "CREATE TRIGGER IF NOT EXISTS note_revision_keep BEFORE UPDATE OF title, body ON note " +
            "WHEN (coalesce(old.title, '') != coalesce(new.title, '') OR old.body != new.body) " +
            "AND (old.body != '' OR coalesce(old.title, '') != '') " +
            "AND NOT EXISTS (SELECT 1 FROM note_revision WHERE note_id = old.id AND kept_at > new.updated_at - 600000) " +
            "BEGIN " +
            "INSERT INTO note_revision(id, note_id, title, body, created_at, kept_at) " +
            "VALUES (lower(hex(randomblob(8))), old.id, old.title, old.body, old.updated_at, new.updated_at); " +
            "DELETE FROM note_revision WHERE note_id = old.id AND id NOT IN " +
            "(SELECT id FROM note_revision WHERE note_id = old.id ORDER BY kept_at DESC LIMIT 20); " +
            "END",
    )
    // Sous-dépenses d'une ligne de liste (portefeuille).
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS expense_entry (" +
            "id TEXT NOT NULL PRIMARY KEY, item_id TEXT NOT NULL, label TEXT NOT NULL, amount INTEGER NOT NULL, " +
            "date TEXT, note TEXT, tx_id TEXT, order_index INTEGER NOT NULL DEFAULT 0, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS expense_entry_item_idx ON expense_entry(item_id, order_index) WHERE is_deleted = 0")
    // Sous-dépense payée tout de suite : la transaction qui l'a débitée.
    tryExec(driver, "ALTER TABLE expense_entry ADD COLUMN tx_id TEXT")
    // Simulations : brouillons chiffrés et leurs lignes (étiquette + expression).
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS simulation (" +
            "id TEXT NOT NULL PRIMARY KEY, name TEXT NOT NULL, note TEXT, order_index INTEGER NOT NULL DEFAULT 0, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS simulation_order_idx ON simulation(order_index) WHERE is_deleted = 0")
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS simulation_line (" +
            "id TEXT NOT NULL PRIMARY KEY, simulation_id TEXT NOT NULL, label TEXT NOT NULL, expression TEXT NOT NULL, " +
            "order_index INTEGER NOT NULL DEFAULT 0, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS simulation_line_sim_idx ON simulation_line(simulation_id, order_index) WHERE is_deleted = 0")
    // Prêts dans les deux sens et leurs remboursements.
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS loan (" +
            "id TEXT NOT NULL PRIMARY KEY, direction TEXT NOT NULL, counterparty TEXT NOT NULL, principal INTEGER NOT NULL, " +
            "interest INTEGER NOT NULL DEFAULT 0, currency TEXT NOT NULL DEFAULT 'XOF', date TEXT NOT NULL, due_date TEXT, note TEXT, " +
            "account_id TEXT, tx_id TEXT, status TEXT NOT NULL DEFAULT 'open', order_index INTEGER NOT NULL DEFAULT 0, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS loan_status_idx ON loan(status, order_index) WHERE is_deleted = 0")
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS loan_payment (" +
            "id TEXT NOT NULL PRIMARY KEY, loan_id TEXT NOT NULL, amount INTEGER NOT NULL, date TEXT NOT NULL, " +
            "account_id TEXT NOT NULL, tx_id TEXT, note TEXT, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS loan_payment_loan_idx ON loan_payment(loan_id, date) WHERE is_deleted = 0")
    // Ressources d'une compétence, lues par l'assistant.
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS skill_resource (" +
            "id TEXT NOT NULL PRIMARY KEY, skill_id TEXT NOT NULL, step_id TEXT, origin TEXT NOT NULL, " +
            "format TEXT NOT NULL, title TEXT NOT NULL, url TEXT, body TEXT, order_index INTEGER NOT NULL DEFAULT 0, " +
            "index_status TEXT, index_error TEXT, indexed_at INTEGER, refresh_error TEXT, push_error TEXT, " +
            "created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS skill_resource_skill_idx ON skill_resource(skill_id, order_index) WHERE is_deleted = 0")
    // Conversations avec l'assistant d'une compétence, et leurs messages.
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS assistant_conversation (" +
            "id TEXT NOT NULL PRIMARY KEY, skill_id TEXT NOT NULL, title TEXT, created_at INTEGER NOT NULL, " +
            "last_message_at INTEGER, push_error TEXT, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS assistant_conversation_skill_idx ON assistant_conversation(skill_id) WHERE is_deleted = 0")
    tryExec(
        driver,
        "CREATE TABLE IF NOT EXISTS assistant_message (" +
            "id TEXT NOT NULL PRIMARY KEY, conversation_id TEXT NOT NULL, role TEXT NOT NULL, content TEXT NOT NULL, " +
            "sources TEXT, status TEXT NOT NULL, error_code TEXT, created_at INTEGER NOT NULL, updated_at INTEGER NOT NULL, " +
            "is_deleted INTEGER NOT NULL DEFAULT 0, deleted_at INTEGER, synced_at INTEGER)",
    )
    tryExec(driver, "CREATE INDEX IF NOT EXISTS assistant_message_conversation_idx ON assistant_message(conversation_id, created_at) WHERE is_deleted = 0")
    // Les articles de blog ont toujours été des notes publiées : la table créée pour eux
    // n'a jamais servi, on la retire des bases qui l'ont.
    tryExec(driver, "DROP TABLE IF EXISTS blog_article")
}

private fun tryExec(driver: SqlDriver, sql: String) {
    try {
        driver.execute(null, sql, 0)
    } catch (_: Throwable) {
        // Colonne/objet déjà présent : migration déjà appliquée, on ignore.
    }
}